
import { useState, useEffect } from 'react'
import {
  VStack,
  HStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Button,
  Select,
  Grid,
  Spinner,
  Box,
  Heading,
  Divider,
  Card,
  CardBody,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  IconButton,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { Mail, Eye, EyeOff, Save, Info } from 'lucide-react'
import { SettingsService, EmailSettingsData } from '@/lib/services/settingsService'
import { useToastNotification } from '@/lib/utils/useToastNotification'

interface EmailSettingsFormProps {
  onSuccess?: () => void
}

// Available mailer options
const mailerOptions = [
  { value: 'smtp', label: 'SMTP' },
  { value: 'mailgun', label: 'Mailgun' },
  { value: 'postmark', label: 'Postmark' },
  { value: 'ses', label: 'Amazon SES' },
  { value: 'microsoft', label: 'Microsoft Graph / Office 365' },
  { value: 'sendmail', label: 'Sendmail' },
  { value: 'log', label: 'Log (Testing)' },
  { value: 'array', label: 'Array (Testing)' },
]

// Encryption options for SMTP
const encryptionOptions = [
  { value: '', label: 'None' },
  { value: 'tls', label: 'TLS' },
  { value: 'ssl', label: 'SSL' },
  { value: 'starttls', label: 'STARTTLS' },
]

// AWS SES regions
const sesRegions = [
  'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
  'eu-west-1', 'eu-west-2', 'eu-west-3', 'eu-central-1',
  'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1', 'ap-northeast-2',
  'sa-east-1', 'ca-central-1', 'ap-south-1', 'eu-north-1',
]

export function EmailSettingsForm({ onSuccess }: EmailSettingsFormProps) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [settings, setSettings] = useState<EmailSettingsData | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [showPassword, setShowPassword] = useState(false)
  const { showError, showSuccess } = useToastNotification()
  const colors = useThemeColors()

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const response = await SettingsService.getEmailSettings()
      
      if (response.success && response.data) {
        const loadedSettings: EmailSettingsData = {
          mailer: response.data.mailer || 'smtp',
          mail_from_name: response.data.mail_from_name || '',
          mail_from_address: response.data.mail_from_address || '',
          smtp: {
            host: response.data.smtp?.host || '',
            port: response.data.smtp?.port || '587',
            encryption: response.data.smtp?.encryption || 'tls',
            username: response.data.smtp?.username || '',
            password: response.data.smtp?.password || '',
          },
          mailgun: {
            domain: response.data.mailgun?.domain || '',
            secret: response.data.mailgun?.secret || '',
          },
          postmark: {
            token: response.data.postmark?.token || '',
          },
          ses: {
            key: response.data.ses?.key || '',
            secret: response.data.ses?.secret || '',
            region: response.data.ses?.region || 'us-east-1',
          },
          microsoft: {
            tenant_id: response.data.microsoft?.tenant_id || '',
            client_id: response.data.microsoft?.client_id || '',
            client_secret: response.data.microsoft?.client_secret || '',
            sender_email: response.data.microsoft?.sender_email || '',
          },
        }
        setSettings(loadedSettings)
        setHasChanges(false)
      } else {
        // Initialize with defaults
        const defaultSettings: EmailSettingsData = {
          mailer: 'smtp',
          mail_from_name: '',
          mail_from_address: '',
          smtp: {
            host: '',
            port: '587',
            encryption: 'tls',
            username: '',
            password: '',
          },
          mailgun: {
            domain: '',
            secret: '',
          },
          postmark: {
            token: '',
          },
          ses: {
            key: '',
            secret: '',
            region: 'us-east-1',
          },
          microsoft: {
            tenant_id: '',
            client_id: '',
            client_secret: '',
            sender_email: '',
          },
        }
        setSettings(defaultSettings)
        setHasChanges(false)
      }
    } catch (err: any) {
      // Initialize with defaults on error
      const defaultSettings: EmailSettingsData = {
        mailer: 'smtp',
        mail_from_name: '',
        mail_from_address: '',
        smtp: {
          host: '',
          port: '587',
          encryption: 'tls',
          username: '',
          password: '',
        },
        mailgun: {
          domain: '',
          secret: '',
        },
        postmark: {
          token: '',
        },
          ses: {
            key: '',
            secret: '',
            region: 'us-east-1',
          },
          microsoft: {
            tenant_id: '',
            client_id: '',
            client_secret: '',
            sender_email: '',
          },
        }
        setSettings(defaultSettings)
        setHasChanges(false)
      showError({ message: err.message || 'Failed to load email settings' })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!settings) return
    
    try {
      setSaving(true)
      setErrors({})
      window.scrollTo({ top: 0, behavior: 'smooth' })
      
      // Only send the configuration for the selected mailer
      const settingsToSend: any = {
        mailer: settings.mailer,
        mail_from_name: settings.mail_from_name,
        mail_from_address: settings.mail_from_address,
      }
      
      // Add only the selected mailer's configuration
      if (settings.mailer === 'smtp') {
        settingsToSend.smtp = settings.smtp
      } else if (settings.mailer === 'mailgun') {
        settingsToSend.mailgun = settings.mailgun
      } else if (settings.mailer === 'postmark') {
        settingsToSend.postmark = settings.postmark
      } else if (settings.mailer === 'ses') {
        settingsToSend.ses = settings.ses
      } else if (settings.mailer === 'microsoft') {
        settingsToSend.microsoft = settings.microsoft
      }
      // sendmail, log, array don't need additional configuration
      
      const response = await SettingsService.updateEmailSettings(settingsToSend)
      if (response.success) {
        showSuccess('Success', response.message || 'Email settings updated successfully')
        await loadSettings()
        setHasChanges(false)
        setLastSaved(new Date())
        setErrors({})
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }, 100)
        onSuccess?.()
      } else {
        throw new Error(response.message || 'Failed to update email settings')
      }
    } catch (err: any) {
      // Handle validation errors
      if (err.response?.data?.errors) {
        const validationErrors = err.response.data.errors
        setErrors(validationErrors)
        
        const errorMessages: string[] = []
        Object.entries(validationErrors).forEach(([field, messages]) => {
          if (Array.isArray(messages)) {
            errorMessages.push(...messages)
          }
        })
        
        if (errorMessages.length > 0) {
          showError({ message: errorMessages.join(', ') })
        } else {
          showError({ message: err.response.data.message || 'Validation failed' })
        }
      } else {
        setErrors({})
        showError(err)
      }
    } finally {
      setSaving(false)
    }
  }

  const updateSetting = (key: keyof EmailSettingsData, value: any) => {
    if (!settings) return
    
    setHasChanges(true)
    setSettings((prev) => {
      if (!prev) return null
      const updated = {
        ...prev,
        [key]: value,
      }
      // Ensure microsoft settings exist when mailer is set to microsoft
      if (key === 'mailer' && value === 'microsoft' && !updated.microsoft) {
        updated.microsoft = {
          tenant_id: '',
          client_id: '',
          client_secret: '',
          sender_email: '',
        }
      }
      return updated
    })
  }

  const updateNestedSetting = (section: 'smtp' | 'mailgun' | 'postmark' | 'ses' | 'microsoft', key: string, value: any) => {
    if (!settings) return
    
    setHasChanges(true)
    setSettings((prev) => {
      if (!prev) return null
      // Ensure the section exists before updating
      const sectionData = prev[section] || (section === 'microsoft' ? {
        tenant_id: '',
        client_id: '',
        client_secret: '',
        sender_email: '',
      } : section === 'smtp' ? {
        host: '',
        port: '587',
        encryption: 'tls',
        username: '',
        password: '',
      } : section === 'mailgun' ? {
        domain: '',
        secret: '',
      } : section === 'postmark' ? {
        token: '',
      } : section === 'ses' ? {
        key: '',
        secret: '',
        region: 'us-east-1',
      } : {})
      
      return {
        ...prev,
        [section]: {
          ...sectionData,
          [key]: value,
        },
      }
    })
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text color={colors.text}>Loading email settings...</Text>
        </VStack>
      </Box>
    )
  }

  if (!settings) {
    return null
  }

  const selectedMailer = settings.mailer

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Card bg={colors.cardBg}>
        <CardBody>
          <HStack justify="space-between" align="center" mb={4}>
            <HStack spacing={3}>
              <Mail size={24} color={colors.heading} />
              <Heading size="md" color={colors.heading}>
                Email Settings
              </Heading>
            </HStack>
            <HStack spacing={2}>
              {lastSaved && (
                <Text fontSize="sm" color={colors.textSecondary}>
                  Last saved: {lastSaved.toLocaleTimeString()}
                </Text>
              )}
              <Button
                leftIcon={<Save size={16} />}
                colorScheme="blue"
                onClick={handleSave}
                isLoading={saving}
                loadingText="Saving..."
                isDisabled={!hasChanges}
              >
                Save Changes
              </Button>
            </HStack>
          </HStack>

          {Object.keys(errors).length > 0 && (
            <Alert status="error" borderRadius="md" mb={4}>
              <AlertIcon />
              <AlertDescription>
                Please fix the errors below before saving.
              </AlertDescription>
            </Alert>
          )}
        </CardBody>
      </Card>

      {/* Mailer Selection */}
      <Card bg={colors.cardBg}>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack spacing={2}>
              <Mail size={20} />
              <Heading size="sm" color={colors.heading}>
                Mail Service Provider
              </Heading>
            </HStack>
            <Divider />

            <FormControl isInvalid={!!errors.mailer}>
              <FormLabel>Email Service</FormLabel>
              <Select
                value={settings.mailer}
                onChange={(e) => updateSetting('mailer', e.target.value)}
                bg={colors.inputBg}
                borderColor={colors.inputBorder}
              >
                {mailerOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <FormHelperText>
                Select the email service provider you want to use
              </FormHelperText>
              <FormErrorMessage>
                {errors.mailer && Array.isArray(errors.mailer) ? errors.mailer[0] : errors.mailer}
              </FormErrorMessage>
            </FormControl>

            {/* From Address Settings */}
            <Divider />
            <Heading size="sm" color={colors.heading} mt={2}>
              From Address
            </Heading>

            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
              <FormControl isInvalid={!!errors.mail_from_name}>
                <FormLabel>From Name</FormLabel>
                <Input
                  value={settings.mail_from_name}
                  onChange={(e) => updateSetting('mail_from_name', e.target.value)}
                  placeholder="Your App Name"
                  bg={colors.inputBg}
                  borderColor={colors.inputBorder}
                />
                <FormHelperText>The name that appears in the "From" field</FormHelperText>
                <FormErrorMessage>
                  {errors.mail_from_name && Array.isArray(errors.mail_from_name) ? errors.mail_from_name[0] : errors.mail_from_name}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.mail_from_address}>
                <FormLabel>From Email Address</FormLabel>
                <Input
                  type="email"
                  value={settings.mail_from_address}
                  onChange={(e) => updateSetting('mail_from_address', e.target.value)}
                  placeholder="noreply@example.com"
                  bg={colors.inputBg}
                  borderColor={colors.inputBorder}
                />
                <FormHelperText>The email address that appears in the "From" field</FormHelperText>
                <FormErrorMessage>
                  {errors.mail_from_address && Array.isArray(errors.mail_from_address) ? errors.mail_from_address[0] : errors.mail_from_address}
                </FormErrorMessage>
              </FormControl>
            </Grid>
          </VStack>
        </CardBody>
      </Card>

      {/* SMTP Settings */}
      {selectedMailer === 'smtp' && (
        <Card bg={colors.cardBg}>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack spacing={2}>
                <Mail size={20} />
                <Heading size="sm" color={colors.heading}>
                  SMTP Configuration
                </Heading>
              </HStack>
              <Divider />

              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                <FormControl isInvalid={!!errors['smtp.host']}>
                  <FormLabel>SMTP Host</FormLabel>
                  <Input
                    value={settings.smtp.host}
                    onChange={(e) => updateNestedSetting('smtp', 'host', e.target.value)}
                    placeholder="smtp.example.com"
                    bg={colors.inputBg}
                    borderColor={colors.inputBorder}
                  />
                  <FormErrorMessage>
                    {errors['smtp.host'] && Array.isArray(errors['smtp.host']) ? errors['smtp.host'][0] : errors['smtp.host']}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors['smtp.port']}>
                  <FormLabel>SMTP Port</FormLabel>
                  <Input
                    value={settings.smtp.port}
                    onChange={(e) => updateNestedSetting('smtp', 'port', e.target.value)}
                    placeholder="587"
                    bg={colors.inputBg}
                    borderColor={colors.inputBorder}
                  />
                  <FormHelperText>Common ports: 587 (TLS), 465 (SSL), 25</FormHelperText>
                  <FormErrorMessage>
                    {errors['smtp.port'] && Array.isArray(errors['smtp.port']) ? errors['smtp.port'][0] : errors['smtp.port']}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors['smtp.encryption']}>
                  <FormLabel>Encryption</FormLabel>
                  <Select
                    value={settings.smtp.encryption}
                    onChange={(e) => updateNestedSetting('smtp', 'encryption', e.target.value)}
                    bg={colors.inputBg}
                    borderColor={colors.inputBorder}
                  >
                    {encryptionOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>
                    {errors['smtp.encryption'] && Array.isArray(errors['smtp.encryption']) ? errors['smtp.encryption'][0] : errors['smtp.encryption']}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors['smtp.username']}>
                  <FormLabel>Username</FormLabel>
                  <Input
                    value={settings.smtp.username}
                    onChange={(e) => updateNestedSetting('smtp', 'username', e.target.value)}
                    placeholder="your-email@example.com"
                    bg={colors.inputBg}
                    borderColor={colors.inputBorder}
                  />
                  <FormErrorMessage>
                    {errors['smtp.username'] && Array.isArray(errors['smtp.username']) ? errors['smtp.username'][0] : errors['smtp.username']}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors['smtp.password']}>
                  <FormLabel>Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={settings.smtp.password}
                      onChange={(e) => updateNestedSetting('smtp', 'password', e.target.value)}
                      placeholder="Your SMTP password"
                      bg={colors.inputBg}
                      borderColor={colors.inputBorder}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        icon={showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>
                    {errors['smtp.password'] && Array.isArray(errors['smtp.password']) ? errors['smtp.password'][0] : errors['smtp.password']}
                  </FormErrorMessage>
                </FormControl>
              </Grid>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Mailgun Settings */}
      {selectedMailer === 'mailgun' && (
        <Card bg={colors.cardBg}>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack spacing={2}>
                <Mail size={20} />
                <Heading size="sm" color={colors.heading}>
                  Mailgun Configuration
                </Heading>
              </HStack>
              <Divider />

              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                <FormControl isInvalid={!!errors['mailgun.domain']}>
                  <FormLabel>Mailgun Domain</FormLabel>
                  <Input
                    value={settings.mailgun.domain}
                    onChange={(e) => updateNestedSetting('mailgun', 'domain', e.target.value)}
                    placeholder="mg.example.com"
                    bg={colors.inputBg}
                    borderColor={colors.inputBorder}
                  />
                  <FormErrorMessage>
                    {errors['mailgun.domain'] && Array.isArray(errors['mailgun.domain']) ? errors['mailgun.domain'][0] : errors['mailgun.domain']}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors['mailgun.secret']}>
                  <FormLabel>Mailgun Secret Key</FormLabel>
                  <Input
                    type="password"
                    value={settings.mailgun.secret}
                    onChange={(e) => updateNestedSetting('mailgun', 'secret', e.target.value)}
                    placeholder="Your Mailgun secret key"
                    bg={colors.inputBg}
                    borderColor={colors.inputBorder}
                  />
                  <FormErrorMessage>
                    {errors['mailgun.secret'] && Array.isArray(errors['mailgun.secret']) ? errors['mailgun.secret'][0] : errors['mailgun.secret']}
                  </FormErrorMessage>
                </FormControl>
              </Grid>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Postmark Settings */}
      {selectedMailer === 'postmark' && (
        <Card bg={colors.cardBg}>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack spacing={2}>
                <Mail size={20} />
                <Heading size="sm" color={colors.heading}>
                  Postmark Configuration
                </Heading>
              </HStack>
              <Divider />

              <FormControl isInvalid={!!errors['postmark.token']}>
                <FormLabel>Postmark Server API Token</FormLabel>
                <Input
                  type="password"
                  value={settings.postmark.token}
                  onChange={(e) => updateNestedSetting('postmark', 'token', e.target.value)}
                  placeholder="Your Postmark API token"
                  bg={colors.inputBg}
                  borderColor={colors.inputBorder}
                />
                <FormHelperText>Find your API token in your Postmark account settings</FormHelperText>
                <FormErrorMessage>
                  {errors['postmark.token'] && Array.isArray(errors['postmark.token']) ? errors['postmark.token'][0] : errors['postmark.token']}
                </FormErrorMessage>
              </FormControl>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* SES Settings */}
      {selectedMailer === 'ses' && (
        <Card bg={colors.cardBg}>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack spacing={2}>
                <Mail size={20} />
                <Heading size="sm" color={colors.heading}>
                  Amazon SES Configuration
                </Heading>
              </HStack>
              <Divider />

              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                <FormControl isInvalid={!!errors['ses.key']}>
                  <FormLabel>AWS Access Key ID</FormLabel>
                  <Input
                    value={settings.ses.key}
                    onChange={(e) => updateNestedSetting('ses', 'key', e.target.value)}
                    placeholder="Your AWS access key"
                    bg={colors.inputBg}
                    borderColor={colors.inputBorder}
                  />
                  <FormErrorMessage>
                    {errors['ses.key'] && Array.isArray(errors['ses.key']) ? errors['ses.key'][0] : errors['ses.key']}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors['ses.secret']}>
                  <FormLabel>AWS Secret Access Key</FormLabel>
                  <Input
                    type="password"
                    value={settings.ses.secret}
                    onChange={(e) => updateNestedSetting('ses', 'secret', e.target.value)}
                    placeholder="Your AWS secret key"
                    bg={colors.inputBg}
                    borderColor={colors.inputBorder}
                  />
                  <FormErrorMessage>
                    {errors['ses.secret'] && Array.isArray(errors['ses.secret']) ? errors['ses.secret'][0] : errors['ses.secret']}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors['ses.region']}>
                  <FormLabel>AWS Region</FormLabel>
                  <Select
                    value={settings.ses.region}
                    onChange={(e) => updateNestedSetting('ses', 'region', e.target.value)}
                    bg={colors.inputBg}
                    borderColor={colors.inputBorder}
                  >
                    {sesRegions.map((region) => (
                      <option key={region} value={region}>
                        {region}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>
                    {errors['ses.region'] && Array.isArray(errors['ses.region']) ? errors['ses.region'][0] : errors['ses.region']}
                  </FormErrorMessage>
                </FormControl>
              </Grid>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Microsoft Graph Settings */}
      {selectedMailer === 'microsoft' && (
        <Card bg={colors.cardBg}>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack spacing={2}>
                <Mail size={20} />
                <Heading size="sm" color={colors.heading}>
                  Microsoft Graph / Office 365 Configuration
                </Heading>
              </HStack>
              <Divider />

              <Alert status="info" borderRadius="md" mb={2}>
                <AlertIcon />
                <AlertDescription>
                  Configure Microsoft Graph API credentials to send emails via Office 365. You'll need to register an application in Azure AD.
                </AlertDescription>
              </Alert>

              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                <FormControl isInvalid={!!errors['microsoft.tenant_id']}>
                  <FormLabel>Tenant ID</FormLabel>
                  <Input
                    value={settings.microsoft?.tenant_id || ''}
                    onChange={(e) => updateNestedSetting('microsoft', 'tenant_id', e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    bg={colors.inputBg}
                    borderColor={colors.inputBorder}
                  />
                  <FormHelperText>Your Azure AD Tenant ID (Directory ID)</FormHelperText>
                  <FormErrorMessage>
                    {errors['microsoft.tenant_id'] && Array.isArray(errors['microsoft.tenant_id']) ? errors['microsoft.tenant_id'][0] : errors['microsoft.tenant_id']}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors['microsoft.client_id']}>
                  <FormLabel>Client ID (Application ID)</FormLabel>
                  <Input
                    value={settings.microsoft?.client_id || ''}
                    onChange={(e) => updateNestedSetting('microsoft', 'client_id', e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    bg={colors.inputBg}
                    borderColor={colors.inputBorder}
                  />
                  <FormHelperText>Your Azure AD Application (Client) ID</FormHelperText>
                  <FormErrorMessage>
                    {errors['microsoft.client_id'] && Array.isArray(errors['microsoft.client_id']) ? errors['microsoft.client_id'][0] : errors['microsoft.client_id']}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors['microsoft.client_secret']}>
                  <FormLabel>Client Secret</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={settings.microsoft?.client_secret || ''}
                      onChange={(e) => updateNestedSetting('microsoft', 'client_secret', e.target.value)}
                      placeholder="Your Azure AD Client Secret"
                      bg={colors.inputBg}
                      borderColor={colors.inputBorder}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={showPassword ? 'Hide secret' : 'Show secret'}
                        icon={showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormHelperText>The client secret value (not the secret ID)</FormHelperText>
                  <FormErrorMessage>
                    {errors['microsoft.client_secret'] && Array.isArray(errors['microsoft.client_secret']) ? errors['microsoft.client_secret'][0] : errors['microsoft.client_secret']}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors['microsoft.sender_email']}>
                  <FormLabel>Sender Email Address</FormLabel>
                  <Input
                    type="email"
                    value={settings.microsoft?.sender_email || ''}
                    onChange={(e) => updateNestedSetting('microsoft', 'sender_email', e.target.value)}
                    placeholder="sender@yourdomain.com"
                    bg={colors.inputBg}
                    borderColor={colors.inputBorder}
                  />
                  <FormHelperText>The Office 365 email address that will send emails</FormHelperText>
                  <FormErrorMessage>
                    {errors['microsoft.sender_email'] && Array.isArray(errors['microsoft.sender_email']) ? errors['microsoft.sender_email'][0] : errors['microsoft.sender_email']}
                  </FormErrorMessage>
                </FormControl>
              </Grid>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Info for other mailers */}
      {(selectedMailer === 'sendmail' || selectedMailer === 'log' || selectedMailer === 'array') && (
        <Card bg={colors.cardBg}>
          <CardBody>
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <AlertDescription>
                {selectedMailer === 'sendmail' && 'Sendmail uses your server\'s sendmail configuration. No additional settings required.'}
                {selectedMailer === 'log' && 'Log mailer writes emails to your log files. Useful for testing. No additional settings required.'}
                {selectedMailer === 'array' && 'Array mailer stores emails in memory. Useful for testing. No additional settings required.'}
              </AlertDescription>
            </Alert>
          </CardBody>
        </Card>
      )}

      {/* Save Button at Bottom */}
      <Card bg={colors.cardBg}>
        <CardBody>
          <HStack justify="flex-end">
            <Button
              leftIcon={<Save size={16} />}
              colorScheme="blue"
              size="lg"
              onClick={handleSave}
              isLoading={saving}
              loadingText="Saving..."
              isDisabled={!hasChanges}
            >
              Save Email Settings
            </Button>
          </HStack>
        </CardBody>
      </Card>
    </VStack>
  )
}

